import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { Badge, Button } from '../../components/ui';
import { Avatar } from '../../components/ui';
import { Icon } from '../../components/icons';
import { BRAND, getThemeColors } from '../../lib/theme';
import { formatDateTime, formatDayMonth, formatTime } from '../../lib/dateFormat';
import {
  msgraphApi,
  type MsFolder, type MsGraphStatus, type MsMessage, type MsMessageDetail,
} from '../../lib/msgraph';
import type { Density } from '../../types';

const FOLDERS: { id: MsFolder; labelKey: string }[] = [
  { id: 'inbox',        labelKey: 'email.folder.inbox' },
  { id: 'sentitems',    labelKey: 'email.folder.sent' },
  { id: 'drafts',       labelKey: 'email.folder.drafts' },
  { id: 'archive',      labelKey: 'email.folder.archive' },
  { id: 'deleteditems', labelKey: 'email.folder.trash' },
];

/** Posteingang: heute → „14:30", sonst „26.10." (immer deutsches Format). */
function formatReceived(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (d.toDateString() === new Date().toDateString()) return formatTime(d, '');
  return formatDayMonth(d, '');
}

interface EmailPageProps {
  dark: boolean;
  density: Density;
}

export function EmailPage({ dark }: EmailPageProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [status, setStatus] = useState<MsGraphStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const [folder, setFolder] = useState<MsFolder>('inbox');
  const [messages, setMessages] = useState<MsMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MsMessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Compose / Reply
  const [composing, setComposing] = useState(false);
  const [compTo, setCompTo] = useState('');
  const [compSubject, setCompSubject] = useState('');
  const [compBody, setCompBody] = useState('');
  const [sending, setSending] = useState(false);
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState('');

  // Verbindungs-Status laden (+ ms_connected / ms_error aus dem OAuth-Redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('ms_error')) {
      setError(i18n.t('email.error.ms_login_failed', { error: params.get('ms_error') }));
    }
    if (params.has('ms_connected') || params.has('ms_error')) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    msgraphApi.status()
      .then(setStatus)
      .catch(() => setStatus({ connected: false, ms_email: null, calendar_enabled: false }))
      .finally(() => setStatusLoading(false));
  }, []);

  const loadMessages = useCallback(async (f: MsFolder) => {
    setMessagesLoading(true);
    setError(null);
    try {
      const data = await msgraphApi.messages(f);
      setMessages(data);
      setSelectedId(prev => prev ?? data[0]?.id ?? null);
    } catch {
      setError(i18n.t('email.error.load_failed'));
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status?.connected) void loadMessages(folder);
  }, [status?.connected, folder, loadMessages]);

  // Detail nachladen
  useEffect(() => {
    if (!selectedId || !status?.connected) { setDetail(null); return; }
    let alive = true;
    setDetailLoading(true);
    msgraphApi.message(selectedId)
      .then(d => { if (alive) setDetail(d); })
      .catch(() => { if (alive) setDetail(null); })
      .finally(() => { if (alive) setDetailLoading(false); });
    return () => { alive = false; };
  }, [selectedId, status?.connected]);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const url = await msgraphApi.connectUrl();
      window.location.href = url;
    } catch {
      setError(i18n.t('email.error.not_configured'));
      setConnecting(false);
    }
  };

  const handleSend = async () => {
    if (!compTo || !compSubject || !compBody) return;
    setSending(true);
    try {
      await msgraphApi.send(compTo, compSubject, compBody);
      setComposing(false);
      setCompTo(''); setCompSubject(''); setCompBody('');
    } catch {
      setError(i18n.t('email.error.send_failed'));
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if (!detail || !reply.trim()) return;
    setSending(true);
    try {
      await msgraphApi.send(detail.from_email, `AW: ${detail.subject}`, reply);
      setReplying(false);
      setReply('');
    } catch {
      setError(i18n.t('email.error.send_failed'));
    } finally {
      setSending(false);
    }
  };

  const filtered = messages.filter(m =>
    !search
    || m.from_name.toLowerCase().includes(search.toLowerCase())
    || m.from_email.toLowerCase().includes(search.toLowerCase())
    || m.subject.toLowerCase().includes(search.toLowerCase()),
  );

  // ── Nicht verbunden: Connect-Screen ────────────────────────────────────────
  if (!statusLoading && status && !status.connected) {
    return (
      <div style={{
        height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 14,
      }}>
        <div style={{ textAlign: 'center', maxWidth: 420, padding: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: t.cardMuted,
            border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 20px',
          }}>
            <Icon.Mail c={BRAND} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: t.heading, marginBottom: 8 }}>
            {tr('email.connect.title')}
          </div>
          <div style={{ fontSize: 13, color: t.muted, lineHeight: 1.7, marginBottom: 24 }}>
            {tr('email.connect.description')}
          </div>
          {error && (
            <div style={{ fontSize: 12, color: '#b42318', marginBottom: 16 }}>{error}</div>
          )}
          <Button variant="primary" dark={dark} disabled={connecting} onClick={() => void startConnect()}>
            {connecting ? tr('email.connect.redirecting') : tr('email.connect.cta')}
          </Button>
        </div>
      </div>
    );
  }

  // ── Loading-Zustand ────────────────────────────────────────────────────────
  if (statusLoading) {
    return (
      <div style={{
        height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 14,
        color: t.muted, fontSize: 13,
      }}>
        {tr('common.loading')}
      </div>
    );
  }

  const selected = messages.find(m => m.id === selectedId) ?? null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '200px 340px 1fr', gap: 0, height: '100%', background: t.card, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden' }}>
      {/* Folders */}
      <div style={{ borderRight: `1px solid ${t.border}`, display: 'flex', flexDirection: 'column', padding: 12 }}>
        <Button variant="primary" dark={dark} icon={<Icon.Plus/>} onClick={() => setComposing(true)} style={{ justifyContent: 'center', marginBottom: 14 }}>
          {tr('email.action.compose')}
        </Button>
        <div style={{ fontSize: 10, fontWeight: 600, color: t.placeholder, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6, paddingLeft: 6 }}>
          {tr('email.section.mailbox')}
        </div>
        {FOLDERS.map(f => (
          <button key={f.id} onClick={() => { setFolder(f.id); setSelectedId(null); }} style={{
            display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between',
            border: 'none', background: folder === f.id ? '#fce7f4' : 'transparent',
            color: folder === f.id ? BRAND : t.text,
            padding: '7px 10px', borderRadius: 7, cursor: 'pointer',
            fontSize: 12, fontWeight: 500, marginBottom: 2, textAlign: 'left', width: '100%',
            fontFamily: 'Outfit, sans-serif',
          }}
            onMouseEnter={e => { if (folder !== f.id) (e.currentTarget as HTMLButtonElement).style.background = t.hover; }}
            onMouseLeave={e => { if (folder !== f.id) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <span>{tr(f.labelKey)}</span>
          </button>
        ))}

        {/* Verbundenes Konto */}
        <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: `1px solid ${t.borderSoft}` }}>
          <div style={{ fontSize: 10, color: t.muted, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={status?.ms_email ?? ''}>
            {status?.ms_email}
          </div>
          {/* Konten, die vor dem Kalender-Sync verbunden wurden, haben den
              Kalender-Scope nicht. E-Mail läuft weiter; für den Kalender ist
              ein einmaliges Neu-Verbinden nötig (neue Microsoft-Zustimmung). */}
          {status?.connected && status.calendar_enabled === false && (
            <div style={{ fontSize: 10, color: '#b54708', marginBottom: 8, lineHeight: 1.4 }}>
              {tr('email.sidebar.calendar_not_shared')}{' '}
              <button
                onClick={() => void startConnect()}
                disabled={connecting}
                style={{ fontSize: 10, color: '#b54708', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontFamily: 'Outfit, sans-serif' }}
              >
                {tr('email.sidebar.reconnect')}
              </button>
              {tr('email.sidebar.calendar_reconnect_suffix')}
            </div>
          )}
          <button
            onClick={() => { void msgraphApi.disconnect().then(() => setStatus({ connected: false, ms_email: null, calendar_enabled: false })); }}
            style={{ fontSize: 11, color: t.muted, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontFamily: 'Outfit, sans-serif' }}
          >
            {tr('email.sidebar.disconnect')}
          </button>
        </div>
      </div>

      {/* Message list */}
      <div style={{ borderRight: `1px solid ${t.border}`, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: 12, borderBottom: `1px solid ${t.borderSoft}`, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 22, top: '50%', transform: 'translateY(-50%)' }}>
            <Icon.Search c={t.placeholder} size={14}/>
          </span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={tr('email.search_placeholder')}
            style={{ width: '100%', fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 8, padding: '8px 10px 8px 32px', background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        {error && (
          <div style={{ padding: '8px 16px', fontSize: 12, color: '#b42318' }}>{error}</div>
        )}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {messagesLoading ? (
            <div style={{ padding: 32, textAlign: 'center', color: t.muted, fontSize: 13 }}>{tr('common.loading')}</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: t.muted, fontSize: 13 }}>{tr('email.empty')}</div>
          ) : filtered.map(m => (
            <div
              key={m.id}
              onClick={() => { setSelectedId(m.id); setReplying(false); setComposing(false); }}
              style={{
                padding: '12px 16px', cursor: 'pointer', borderBottom: `1px solid ${t.borderSoft}`,
                background: selectedId === m.id ? (dark ? '#1a1220' : '#fdf2f8') : 'transparent',
                borderLeft: selectedId === m.id ? `3px solid ${BRAND}` : '3px solid transparent',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  {!m.is_read && <span style={{ width: 6, height: 6, borderRadius: '50%', background: BRAND, flexShrink: 0 }}/>}
                  <span style={{ fontSize: 13, fontWeight: m.is_read ? 500 : 700, color: t.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.from_name || m.from_email}
                  </span>
                </div>
                <span style={{ fontSize: 10, color: t.muted, flexShrink: 0 }}>{formatReceived(m.received_at)}</span>
              </div>
              <div style={{ fontSize: 12, fontWeight: m.is_read ? 500 : 600, color: t.text, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.subject}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: t.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{m.preview}</span>
                {m.has_attachments && <Icon.Paperclip c={t.muted}/>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reader / Compose */}
      {composing ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 24px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: t.heading }}>{tr('email.compose.title')}</span>
            <button onClick={() => setComposing(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Icon.Close c={t.muted} size={18}/></button>
          </div>
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', borderBottom: `1px solid ${t.borderSoft}`, paddingBottom: 8 }}>
              <label style={{ width: 60, fontSize: 12, fontWeight: 500, color: t.muted }}>{tr('email.compose.to')}</label>
              <input value={compTo} onChange={e => setCompTo(e.target.value)} placeholder={tr('email.compose.to_placeholder')} style={{ flex: 1, border: 'none', background: 'transparent', color: t.heading, fontSize: 13, fontFamily: 'Outfit,sans-serif', outline: 'none' }}/>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', borderBottom: `1px solid ${t.borderSoft}`, paddingBottom: 8 }}>
              <label style={{ width: 60, fontSize: 12, fontWeight: 500, color: t.muted }}>{tr('email.compose.subject')}</label>
              <input value={compSubject} onChange={e => setCompSubject(e.target.value)} placeholder={tr('email.compose.subject_placeholder')} style={{ flex: 1, border: 'none', background: 'transparent', color: t.heading, fontSize: 13, fontFamily: 'Outfit,sans-serif', outline: 'none' }}/>
            </div>
            <textarea value={compBody} onChange={e => setCompBody(e.target.value)} placeholder={tr('email.compose.body_placeholder')} style={{ flex: 1, border: 'none', background: 'transparent', color: t.text, fontSize: 13, fontFamily: 'Outfit,sans-serif', resize: 'none', lineHeight: 1.7, marginTop: 8, outline: 'none' }}/>
          </div>
          <div style={{ padding: 14, borderTop: `1px solid ${t.border}`, display: 'flex', gap: 8, background: t.cardMuted }}>
            <Button variant="primary" dark={dark} disabled={sending || !compTo || !compSubject || !compBody} onClick={() => void handleSend()}>
              {sending ? '…' : tr('email.action.send')}
            </Button>
            <Button variant="secondary" dark={dark} onClick={() => setComposing(false)}>{tr('common.action.cancel')}</Button>
          </div>
        </div>
      ) : selected ? (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: '16px 24px', borderBottom: `1px solid ${t.borderSoft}` }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.heading, marginBottom: 5 }}>{selected.subject}</div>
            {selected.has_attachments && <Badge tone="neutral" dark={dark}>{tr('email.action.attach')}</Badge>}
          </div>

          <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
              <Avatar name={selected.from_name || selected.from_email} size={40} tone="brand" dark={dark}/>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{selected.from_name || selected.from_email}</div>
                    <div style={{ fontSize: 11, color: t.muted }}>{selected.from_email}</div>
                  </div>
                  <div style={{ fontSize: 11, color: t.muted }}>
                    {formatDateTime(selected.received_at, '')}
                  </div>
                </div>
              </div>
            </div>

            {detailLoading ? (
              <div style={{ fontSize: 13, color: t.muted, paddingLeft: 52 }}>{tr('common.loading')}</div>
            ) : detail ? (
              detail.body_type === 'html' ? (
                <iframe
                  title={tr('email.reader.body_title')}
                  sandbox=""
                  srcDoc={detail.body_html}
                  style={{ width: '100%', minHeight: 400, border: 'none', background: '#fff', borderRadius: 8 }}
                />
              ) : (
                <div style={{ fontSize: 14, color: t.text, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>{detail.body_html}</div>
              )
            ) : (
              <div style={{ fontSize: 13, color: t.muted }}>{selected.preview}</div>
            )}

            {replying && (
              <div className="fade-in" style={{ marginTop: 18, background: t.cardMuted, border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.borderSoft}`, fontSize: 12, color: t.muted }}>
                  {tr('email.reply_to_prefix')} <strong style={{ color: t.text }}>{selected.from_name || selected.from_email}</strong>
                </div>
                <textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  autoFocus
                  placeholder={tr('email.reply_placeholder')}
                  style={{ width: '100%', minHeight: 100, border: 'none', background: 'transparent', padding: 14, color: t.heading, fontSize: 13, fontFamily: 'Outfit,sans-serif', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
                />
                <div style={{ padding: 10, borderTop: `1px solid ${t.borderSoft}`, display: 'flex', gap: 8 }}>
                  <Button variant="primary" dark={dark} size="sm" disabled={sending || !reply.trim()} onClick={() => void handleReply()}>
                    {sending ? '…' : tr('email.action.send')}
                  </Button>
                  <Button variant="secondary" dark={dark} size="sm" onClick={() => { setReplying(false); setReply(''); }}>{tr('common.action.cancel')}</Button>
                </div>
              </div>
            )}
          </div>

          {!replying && (
            <div style={{ padding: 14, borderTop: `1px solid ${t.border}`, display: 'flex', gap: 8, background: t.cardMuted }}>
              <Button variant="primary" dark={dark} icon={<Icon.Reply c="#fff"/>} onClick={() => setReplying(true)}>{tr('email.action.reply')}</Button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.muted, fontSize: 14 }}>
          {tr('email.no_selection')}
        </div>
      )}
    </div>
  );
}
